import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AdminService } from '../admin/admin.service';
import { DataToolsApps } from '../shared/DataToolsApps';
import { UtilsService } from '../shared/services/utils.service';
import { AllPrimaryEntitiesNames, AllPrimaryEntitiesTypes, DataRuleId, RuleData } from './data-rules-model';
import { IRuleOptionsItem } from './lib/option-items-providers';
import { MetaDataService } from './meta-data.service';

export class DataRulesStaticService {
  static credentialingEntities: string[] = [
    'CertificationRequirement',
    'CertificationFileAttachment',
    'CandidateCertification'
  ];

  static bullhornRules(rules: RuleData[]): RuleData[] {
    rules = rules.filter(rule => rule.type === 'BullhornBullhornUpdate');
    DataRulesStaticService.convertDatesFromISOFormat(rules);
    console.log('KPDR: Rules (BH2BH only)', rules.length);
    return rules;
  }

  static convertDatesFromISOFormat(rules: RuleData[]): RuleData[] {
    rules.forEach(rule => {
      rule.action.config.steps = rule.action.config.steps.map(step => {
        if (step.type === 'REPLACE' && step.destinationType === 'DATE')
          step.value = DataRulesStaticService.removeTimeInfo(step.value);
        return step;
      });

      rule.source.config.criteria = rule.source.config.criteria.map(criteria => {
        if (criteria.fieldType === 'DATE' && (criteria.operator !== 'within' && criteria.operator !== 'notWithin'))
          criteria.value = DataRulesStaticService.removeTimeInfo(criteria.value);
        return criteria;
      });
    });

    return rules;
  }

  static convertDatesToUTCFormat(rule: RuleData): void {
    rule.action.config.steps = rule.action.config.steps.map(step => {
      if (step.type === 'REPLACE' && step.destinationType === 'DATE') {
        step.value = new Date(step.value).toISOString().slice(0, -1) + 'UTC';
      }
      return step;
    });

    rule.source.config.criteria = rule.source.config.criteria.map(criteria => {
      if (criteria.fieldType === 'DATE' && (criteria.operator !== 'within' && criteria.operator !== 'notWithin'))
        criteria.value = new Date(criteria.value).toISOString();
      return criteria;
    });
  }

  static removeTimeInfo(datetimeISO: string): string {
    const tIndex = datetimeISO.indexOf('T');
    return tIndex > -1 ? datetimeISO.substr(0, tIndex) : datetimeISO;
  }
}

abstract class AbstractDataRulesDataService {

  // Originally _rules is undefined and ll never be that then, once it changed to anything of the specified types
  // DO NOT MANUALLY SET THAT PROPERTY TO UNDEFINED ANYWHERE
  private _rules: RuleData[] | null;
  private _loading: boolean;

  private _availableEntities: AllPrimaryEntitiesNames[];

  protected constructor(
    protected utilsService: UtilsService,
    protected adminService: AdminService,
    protected metaDataService: MetaDataService
  ) {
  }

  static ruleIdSort(a: DataRuleId, b: DataRuleId): (-1 | 1 | 0) {
    if (`${a}` === '0') return 1;
    if (`${b}` === '0') return -1;
    if (`${a}` < `${b}`) return -1;
    if (`${a}` > `${b}`) return 1;
    return 0;
  }

  static ruleNameSort(a: string, b: string): (-1 | 1 | 0) {

    // On the UI originally was
    // if (a.ruleData.name > b.ruleData.name) return 1;
    // if (a.ruleData.name < b.ruleData.name) return -1;
    // return 0;

    return a.toUpperCase().localeCompare(b.toUpperCase()) as (-1 | 1 | 0);
  }

  protected abstract loadAllRules(): Observable<RuleData[]>;
  protected abstract saveRuleAction(data: RuleData): Observable<RuleData>;
  protected abstract deleteRuleAction(id: string): Observable<void>;

  protected log(message: string): void {
    console.log(`DataRulesData service: ${message}`);
  }

  protected logError(error: string): void {
    console.error(`DataRulesData service: ${error}`);
  }

  private get isNoRules(): boolean {
    return this._rules === null;
  }

  public get loading(): boolean {
    return this._loading === true;
  }

  public get isInitialisedOnce(): boolean {
    return typeof this._rules !== 'undefined';
  }
  public get availableEntities(): AllPrimaryEntitiesNames[] {
    return typeof this._availableEntities === 'undefined'
      ? []
      : [...this._availableEntities];
  }

  public get allRules(): RuleData[] {
    return this.isNoRules || !this.isInitialisedOnce ? [] : [...this._rules];
  }

  private getRuleIndex(ruleId: DataRuleId): number {
    return this.allRules.findIndex(rule => this.utilsService.compareInternalDataRuleIds(rule.id, ruleId));
  }

  initialize(force: boolean): Observable<void> {
    if (!force && this.isInitialisedOnce) {
      return of(undefined);
    }

    // What does this do? Immediately return undefined then waits 200ms and if not loading then returns undefined
    if (!force && this.loading) {
      // TODO Surely not the best way to wait happening loadings, any better decisions are welcome
      let waiter = setTimeout(() => {
        if (!this.loading) {
          waiter = undefined;
          return of(undefined);
        }
      }, 200);
      return of(undefined);
    }

    // that flag means only "force" or first-time initialize process
    this._loading = true;

    return this.loadAllRules().pipe(
        map(rules => {
          rules = rules ?? [];

          rules = DataRulesStaticService.bullhornRules(rules);

          let availableEntities: AllPrimaryEntitiesNames[] = this.metaDataService.getEntities() as AllPrimaryEntitiesNames[];
          if (!this.adminService.checkAppEnabled(DataToolsApps.DATARULES_CREDENTIALLING)) {
            const isCredentialEntity = (entity: string) => DataRulesStaticService
                .credentialingEntities.find(credentialingEntity => (credentialingEntity === entity));
            availableEntities = availableEntities.filter(entity => !isCredentialEntity(entity));
            rules = rules.filter(rule => !isCredentialEntity(rule.source.config.entity.toString()));
          }
          this._availableEntities = availableEntities;

          // isNoRules relies on the value being null
          this._rules = rules.length > 0 ? rules : null;
          this.sortAllRules();
        }),
        tap(_ => {
          this._loading = false;
          this.log(`rules amount: ${this.allRules.length}`);
          this.log('initialize complete');
        }));
  }

  private sortAllRules(): void {
    this._rules?.sort((a, b) => DataRulesDataService.ruleNameSort(a.name, b.name));
  }

  public getRule(id: DataRuleId): RuleData {
    return this.allRules.find(rule => this.utilsService.compareInternalDataRuleIds(rule.id, id));
  }

  public getRules(ids: DataRuleId[]): RuleData[] {
    return this.allRules.filter(rule => ids.findIndex(id => this.utilsService.compareInternalDataRuleIds(id, rule.id)) > -1);
  }

  public getRuleOptionsForEntity(entity?: AllPrimaryEntitiesNames): IRuleOptionsItem[] {
    return this.allRules
      .filter((rule) => {
        const isEntityNameProvided = !!rule.source?.config?.entity;
        const isRuleSaved = !this.utilsService.compareInternalDataRuleIds(rule.id, 0);
        let isEntityMatching = true;
        if (entity) {
          if (isEntityNameProvided) {
            isEntityMatching = rule.source.config.entity === entity;
          } else {
            isEntityMatching = false;
          }
        }
        return isEntityNameProvided && isRuleSaved && isEntityMatching;
      })
      .map<IRuleOptionsItem>(rule => {
        return ({
          value: rule.id as string,
          label: rule.name.toUpperCase(),
          entity: rule.source.config.entity
        });
      });
  }

  saveRule(data: RuleData): Observable<string> {
    DataRulesStaticService.convertDatesToUTCFormat(data);

    return this.saveRuleAction(data)
      .pipe(
        map(savedRule => DataRulesStaticService.convertDatesFromISOFormat([savedRule])[0]),
        map(savedRuleData => {
          const updatedRuleIndex = this.getRuleIndex(data.id);
          if (updatedRuleIndex > -1) {
            const existingRule = this._rules[updatedRuleIndex];
            this._rules[updatedRuleIndex] = savedRuleData;
            if (!savedRuleData.tagIds && existingRule.tagIds) {
              this._rules[updatedRuleIndex].tagIds = existingRule.tagIds;
            }
            if (!savedRuleData.folders && existingRule.folders) {
              this._rules[updatedRuleIndex].folders = existingRule.folders;
            }
          } else {
            if (!this._rules)
              this._rules = [];
            if (!savedRuleData.folders && data.folders) {
              savedRuleData.folders = data.folders;
            }
            if (!savedRuleData.tagIds && data.tagIds) {
              savedRuleData.tagIds = data.tagIds;
            }
            this._rules.push(savedRuleData);
          }
          this.sortAllRules();
          return savedRuleData.id as string;
        })
      );
  }

  deleteRule(id: DataRuleId): Observable<void> {
    if (this.isNoRules)
      return of(undefined);

    const ruleIndex = this.getRuleIndex(id);

    if (ruleIndex === -1) {
      // TODO Handle the error messages
      return of(undefined);
    }

    const source = this.utilsService.compareInternalDataRuleIds(id, 0) ? of(undefined) : this.deleteRuleAction(id as string);

    return source.pipe(
      map(() => {
        this._rules.splice(ruleIndex, 1);
      })
    );
  }
}

@Injectable({
  providedIn: 'root'
})
export class DataRulesDataService extends AbstractDataRulesDataService {

  constructor(private http: HttpClient,
              protected utilsService: UtilsService,
              protected adminService: AdminService,
              protected metaDataService: MetaDataService) {
    super(utilsService, adminService, metaDataService);
  }

  protected loadAllRules(): Observable<RuleData[]> {
    return this.http.get<RuleData[]>(`${environment.dataRulesUrl}/rule`).pipe(
      tap(_ => console.timeEnd('getAllRules')),
      tap(allRules => this.log(`KPDR: Rules ${allRules.length}`))
    );
  }

  protected saveRuleAction(rule: RuleData): Observable<RuleData> {

    let httpResponse: Observable<any>;
    if (this.utilsService.compareInternalDataRuleIds(rule.id, 0))
      httpResponse = this.http.post<any>(`${environment.dataRulesUrl}/rule`, rule);             // insert
    else
      httpResponse = this.http.put<any>(`${environment.dataRulesUrl}/rule/${rule.id}`, rule);   // update

    return httpResponse;
  }

  protected deleteRuleAction(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.dataRulesUrl}/rule/${id}`);
  }

}
